import { useState } from 'react';
import type { ApiKeyDefaults, ApiKeyDtoT, ApiKeyScope, WebhookEventType } from '@wave/shared';
import { API_KEY_SCOPES, WEBHOOK_EVENT_TYPES } from '@wave/shared';
import { useCreateApiKey, useRegenerateWebhookSecret, useUpdateApiKey } from '../../api/integration';
import { btnGhostSm, btnPrimary, fieldLabelCls, inputCls } from '../../lib/styles';
import { Modal } from '../Modal';
import { SCOPE_LABELS, WEBHOOK_EVENT_LABELS } from './labels';

const pillCls = (active: boolean) =>
  `flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors duration-150 cursor-pointer ${
    active
      ? 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
      : 'border-border bg-bg text-text2 hover:bg-bg3'
  }`;

interface Props {
  /** null — создание нового ключа; иначе — редактирование существующего. */
  apiKey: ApiKeyDtoT | null;
  onClose: () => void;
}

// Секрет, который нужно показать один раз (после create/update/regenerate) —
// живёт только в стейте модалки, теряется при закрытии, нигде не персистится.
interface RevealState {
  title: string;
  plainKey?: string;
  webhookSecret?: string;
}

export function ApiKeyModal({ apiKey, onClose }: Props) {
  const isEdit = !!apiKey;
  const create = useCreateApiKey();
  const update = useUpdateApiKey();
  const regenerate = useRegenerateWebhookSecret();

  const [name, setName] = useState(apiKey?.name ?? '');
  const [enabled, setEnabled] = useState(apiKey?.enabled ?? true);
  const [scopes, setScopes] = useState<ApiKeyScope[]>(apiKey?.scopes ?? ['orders:create']);
  const [defaultClient, setDefaultClient] = useState(apiKey?.defaults.client ?? '');
  const [defaultExecutor, setDefaultExecutor] = useState(apiKey?.defaults.executor ?? '');
  const [defaultOperator, setDefaultOperator] = useState(apiKey?.defaults.operator ?? '');
  const [webhookUrl, setWebhookUrl] = useState(apiKey?.webhookUrl ?? '');
  const [webhookEvents, setWebhookEvents] = useState<WebhookEventType[]>(apiKey?.webhookEvents ?? []);
  const [error, setError] = useState<string | null>(null);

  // Экран «секрет показывается один раз» — после успешного submit модалка
  // переключается на него вместо закрытия (create — всегда; update/regenerate —
  // только если сервер вернул новый webhookSecret).
  const [reveal, setReveal] = useState<RevealState | null>(null);

  function toggleScope(s: ApiKeyScope) {
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  function toggleEvent(e: WebhookEventType) {
    setWebhookEvents((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));
  }

  function buildDefaults(): ApiKeyDefaults {
    return {
      ...(defaultClient.trim() ? { client: defaultClient.trim() } : {}),
      ...(defaultExecutor.trim() ? { executor: defaultExecutor.trim() } : {}),
      ...(defaultOperator.trim() ? { operator: defaultOperator.trim() } : {}),
    };
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (scopes.length === 0) {
      setError('Выберите хотя бы один скоуп');
      return;
    }
    const payload = {
      name,
      scopes,
      defaults: buildDefaults(),
      webhookUrl: webhookUrl.trim() || null,
      webhookEvents,
    };
    try {
      if (isEdit) {
        const result = await update.mutateAsync({ id: apiKey!.id, input: { ...payload, enabled } });
        if (result.webhookSecret) {
          setReveal({ title: 'Webhook-секрет обновлён', webhookSecret: result.webhookSecret });
        } else {
          onClose();
        }
      } else {
        const result = await create.mutateAsync(payload);
        setReveal({ title: 'Ключ создан', plainKey: result.plainKey, webhookSecret: result.webhookSecret });
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function handleRegenerate() {
    if (!apiKey) return;
    if (!confirm('Перегенерировать webhook-секрет? Старый секрет перестанет работать.')) return;
    setError(null);
    try {
      const result = await regenerate.mutateAsync(apiKey.id);
      setReveal({ title: 'Webhook-секрет обновлён', webhookSecret: result.webhookSecret });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (reveal) {
    return (
      <Modal title={reveal.title} onClose={onClose}>
        <div className="space-y-4">
          {reveal.plainKey && <SecretBlock label="API-ключ" value={reveal.plainKey} />}
          {reveal.webhookSecret && <SecretBlock label="Webhook-секрет" value={reveal.webhookSecret} />}
          <div className="rounded-md border border-[var(--yellow-border)] bg-[var(--yellow-bg)] px-3 py-2 text-sm text-warning">
            Ключ показывается один раз — сохраните его. Восстановить позже нельзя, только перевыпустить.
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className={btnPrimary}>
              Готово
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={isEdit ? `Ключ «${apiKey!.name}»` : 'Новый API-ключ'} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} className="space-y-4">
        <div>
          <label className={fieldLabelCls}>Имя</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`${inputCls} w-full`}
          />
        </div>

        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-text">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Ключ активен
          </label>
        )}

        <div>
          <span className={fieldLabelCls}>Скоупы</span>
          <div className="flex flex-wrap gap-2">
            {API_KEY_SCOPES.map((s) => (
              <label key={s} className={pillCls(scopes.includes(s))}>
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={() => toggleScope(s)}
                  className="h-3.5 w-3.5 accent-accent"
                />
                {SCOPE_LABELS[s]}
              </label>
            ))}
          </div>
        </div>

        <div>
          <span className={fieldLabelCls}>Значения по умолчанию (если внешняя система их не передаёт)</span>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <input
              value={defaultClient}
              onChange={(e) => setDefaultClient(e.target.value)}
              placeholder="код счёта (клиент)"
              className={`${inputCls} w-full font-mono`}
            />
            <input
              value={defaultExecutor}
              onChange={(e) => setDefaultExecutor(e.target.value)}
              placeholder="код счёта (исполнитель)"
              className={`${inputCls} w-full font-mono`}
            />
            <input
              value={defaultOperator}
              onChange={(e) => setDefaultOperator(e.target.value)}
              placeholder="оператор"
              className={`${inputCls} w-full`}
            />
          </div>
        </div>

        <div className="space-y-3 rounded-md border border-border bg-bg2 p-3">
          <div>
            <label className={fieldLabelCls}>Webhook URL</label>
            <input
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://example.com/webhook"
              className={`${inputCls} w-full font-mono text-xs`}
            />
          </div>
          <div>
            <span className={fieldLabelCls}>События</span>
            <div className="flex flex-wrap gap-2">
              {WEBHOOK_EVENT_TYPES.map((ev) => (
                <label key={ev} className={pillCls(webhookEvents.includes(ev))}>
                  <input
                    type="checkbox"
                    checked={webhookEvents.includes(ev)}
                    onChange={() => toggleEvent(ev)}
                    className="h-3.5 w-3.5 accent-accent"
                  />
                  {WEBHOOK_EVENT_LABELS[ev]}
                </label>
              ))}
            </div>
          </div>
          {isEdit && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-text2">Webhook-секрет:</span>
              {apiKey!.webhookSecretSet ? (
                <span className="text-success">задан</span>
              ) : (
                <span className="text-text-subtle">не задан</span>
              )}
              <button
                type="button"
                onClick={() => void handleRegenerate()}
                disabled={regenerate.isPending || !apiKey!.webhookSecretSet}
                className={btnGhostSm}
              >
                {regenerate.isPending ? '…' : 'Перегенерировать'}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <div className="flex justify-end border-t border-border-subtle pt-4">
          <button type="submit" disabled={create.isPending || update.isPending} className={btnPrimary}>
            {isEdit
              ? update.isPending
                ? 'Сохранение…'
                : 'Сохранить'
              : create.isPending
                ? 'Создание…'
                : 'Создать'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SecretBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[var(--accent-border)] bg-[var(--accent-bg)] p-4">
      <div className="mb-2 text-sm font-medium text-text">{label}</div>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 overflow-x-auto rounded-md border border-border bg-bg px-3 py-1.5 font-mono text-sm text-text">
          {value}
        </code>
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(value)}
          className={btnGhostSm}
          title="Скопировать"
        >
          ⧉
        </button>
      </div>
    </div>
  );
}
